/**
 * The user's calendar settings as props of the time grid: hour height / fit to working hours, working-time
 * shading, the selection label with its length and the second time zone column.
 */
import { useI18n } from '@platform/i18n';
import type { CalendarTimeGridProps } from '@platform/ui';
import { useMemo } from 'react';
import { useCalendar } from './calendar-context';
import { useCalendarFormat } from './format';
import { zoneLabel } from './zones';

export const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;


type GridSettings = Pick<CalendarTimeGridProps, 'hourHeight' | 'fitWorkingHours' | 'workingHours' | 'formatSelection' | 'secondaryZone' | 'primaryZoneLabel'>;

export function useTimeGridSettings(): GridSettings {
  const { formatDate, locale } = useI18n();
  const format = useCalendarFormat();
  const { settings, date } = useCalendar();
  const { hourHeight, fitWorkingHours, workingStartMinutes, workingEndMinutes, workingDays, secondaryTimeZone, timeFormat } = settings;
  return useMemo(() => {
    const cycle: Intl.DateTimeFormatOptions = timeFormat === 'h12' ? { hourCycle: 'h12' } : timeFormat === 'h24' ? { hourCycle: 'h23' } : {};
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return {
      hourHeight,
      fitWorkingHours,
      workingHours: { startMinutes: workingStartMinutes, endMinutes: workingEndMinutes, days: workingDays.map((d) => WEEKDAY_NAMES.indexOf(d)) },
      formatSelection: ({ start, end, allDay }) => format.selection(start, end, allDay),
      ...(secondaryTimeZone && {
        secondaryZone: {
          label: zoneLabel(locale, secondaryTimeZone, day),
          formatHour: (hour: number) => {
            const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour);
            // Whole-hour zones show "15", others "15:30".
            const minutes = new Intl.DateTimeFormat('en', { timeZone: secondaryTimeZone, minute: 'numeric' }).format(at);
            return formatDate(at, { timeZone: secondaryTimeZone, hour: 'numeric', ...(minutes !== '0' && { minute: '2-digit' }), ...cycle });
          },
        },
        primaryZoneLabel: zoneLabel(locale, settings.displayTimeZone ?? undefined, day),
      }),
    };
  }, [hourHeight, fitWorkingHours, workingStartMinutes, workingEndMinutes, workingDays, secondaryTimeZone, timeFormat, settings.displayTimeZone, date, format, formatDate, locale]);
}
